<!--
  - Copyright (c) 2024-2025. Haodong JU
  -->

<template>
  <div>
    <page-header>
      特殊价格
      <template #subtitle>
        包厢在指定时段的加价。这里按每小时显示和填写（云端按每 15 分钟存，= 每小时 ÷ 4）。
        <template v-if="businessHourOffset !== null">
          时间按实际时间显示。
        </template>
        <span
          v-else
          class="orange--text text--darken-3"
        >时间是后台营业时间（没取到营业日偏移，未换算成实际时间）。</span>
      </template>
      <template #action>
        <primary-button
          icon="mdi-plus"
          text="新建特殊价格"
          @click="openNew"
        />
      </template>
    </page-header>
    <div
      v-if="hasAnyEffectiveDate"
      class="d-flex flex-wrap mt-4"
      style="gap: 8px"
    >
      <v-chip
        v-for="f in periodFilters"
        :key="f.value"
        :color="periodFilter === f.value ? 'primary' : 'grey lighten-3'"
        :text-color="periodFilter === f.value ? 'white' : 'black'"
        small
        @click="periodFilter = f.value"
      >
        {{ f.text }} · {{ f.count }}
      </v-chip>
    </div>
    <loading-provider
      :loading="loading"
      class="mt-4"
    >
      <div
        v-if="!loading && list.length === 0"
        class="text-body-2 grey--text pa-4"
      >
        还没有特殊价格
      </div>
      <div
        v-else-if="!loading && roomGroups.length === 0"
        class="text-body-2 grey--text pa-4"
      >
        没有{{ currentFilterText }}的特殊价格
      </div>
      <v-card
        v-for="room in roomGroups"
        :key="room.tableId"
        class="mb-4 sp-room"
        elevation="0"
        outlined
      >
        <div class="d-flex align-center px-4 pt-3 pb-2">
          <v-icon
            class="mr-2"
            small
          >
            mdi-door
          </v-icon>
          <div class="text-subtitle-1 font-weight-black">
            {{ room.name }}
          </div>
          <div class="text-caption grey--text ml-2">
            {{ room.count }} 条
          </div>
        </div>
        <v-divider />
        <div
          v-for="(row, ri) in room.rows"
          :key="row.key"
          :class="['sp-row', ri > 0 ? 'sp-row-border' : '']"
        >
          <div class="sp-row-label">
            <div class="font-weight-bold">
              {{ row.label }}
            </div>
            <div
              v-if="row.sub"
              class="text-caption grey--text"
            >
              {{ row.sub }}
            </div>
          </div>
          <div class="sp-row-slots">
            <div
              v-for="r in row.rules"
              :key="r.id"
              :class="['sp-slot', r.active ? '' : 'sp-slot-inactive']"
              @click="edit(r)"
            >
              <div class="sp-slot-time">
                {{ timeRangeLabel(r) }}
              </div>
              <div class="sp-slot-price">
                <span class="font-weight-bold">每小时 +{{ hourlyPrice(r) | priceDisplay }}</span>
                <span class="grey--text text--darken-1 ml-2">
                  整段 {{ durationLabel(r) }} 合计 +{{ totalPrice(r) | priceDisplay }}
                </span>
              </div>
              <div
                class="d-flex flex-wrap align-center"
                style="gap: 4px"
              >
                <v-chip
                  v-if="r.effectiveFrom || r.effectiveTo"
                  :color="periodInfo(r).color"
                  outlined
                  x-small
                >
                  {{ periodLabel(r) }} · {{ periodInfo(r).text }}
                </v-chip>
                <v-chip
                  v-if="!r.active"
                  color="grey"
                  text-color="white"
                  x-small
                >
                  未激活
                </v-chip>
                <v-btn
                  icon
                  small
                  title="删除"
                  @click.stop="remove(r)"
                >
                  <v-icon small>
                    mdi-delete-outline
                  </v-icon>
                </v-btn>
              </div>
            </div>
          </div>
        </div>
      </v-card>
      <div
        v-if="list.length > 0"
        class="text-caption grey--text px-1"
      >
        同一包厢同一时段命中多条时：指定日期 &gt; 每周 &gt; 每天；同类型里带生效日期的优先。设了「节假日按周几」的日子，每周规则按指定的周几算。
      </div>
    </loading-provider>
    <v-dialog
      v-model="showDialog"
      max-width="560"
      :fullscreen="$vuetify.breakpoint.xsOnly"
    >
      <v-card v-if="form">
        <!-- 手机上全屏弹窗会把 v-card 的 padding 清零，所以内边距放在里面这一层 -->
        <div style="padding: 16px">
          <div class="d-flex align-center mb-4">
            <div
              class="font-weight-bold"
              style="font-size: 20px"
            >
              {{ form.id ? '编辑特殊价格' : '新建特殊价格' }}
            </div>
            <v-spacer />
            <v-btn
              icon
              @click="showDialog = false"
            >
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </div>
          <v-form
            ref="form"
            @submit.prevent
          >
            <v-select
              v-model="form.tableId"
              :items="roomItems"
              :rules="[v => !!v || '请选择包厢']"
              dense
              label="包厢"
              outlined
            />
            <v-select
              v-model="form.dateType"
              :items="dateTypeItems"
              dense
              label="类型"
              outlined
            />
            <v-select
              v-if="form.dateType === 'WEEKLY'"
              v-model="form.weekdays"
              :hint="form.id ? '多选时，其他周几会各新建一条相同的规则' : '可多选，每个周几各存一条'"
              :items="weekdayItems"
              :rules="[v => (v && v.length > 0) || '请选择周几']"
              chips
              dense
              label="周几"
              multiple
              outlined
              persistent-hint
              small-chips
            />
            <v-text-field
              v-if="form.dateType === 'SPECIFIC_DATE'"
              v-model="form.specificDate"
              :rules="[v => !!v || '请选择日期']"
              dense
              hint="营业日（按营业日偏移折算后的那一天）"
              label="日期"
              outlined
              persistent-hint
              type="date"
            />
            <div
              class="d-flex flex-wrap mt-2"
              style="gap: 0 12px"
            >
              <business-time-select
                v-model="form.startTime"
                :label="'开始' + timeKindSuffix"
                :offset="businessHourOffset"
                :rules="[v => !!v || '请选择开始时间']"
                class="sp-field-half"
                dense
              />
              <business-time-select
                v-model="form.endTime"
                :label="'结束（不含）' + timeKindSuffix"
                :offset="businessHourOffset"
                :rules="[v => !!v || '请选择结束时间', () => timeRangeError || true]"
                class="sp-field-half"
                dense
              />
            </div>
            <v-text-field
              v-model="form.pricePerHour"
              :rules="[v => isNumber(v) || '请填写数字']"
              dense
              label="每小时加价"
              outlined
              prefix="€"
              step="0.01"
              type="number"
            />
            <div class="text-body-2 mb-4 mt-n2 grey--text text--darken-2">
              <template v-if="isNumber(form.pricePerHour)">
                存为每 15 分钟 +{{ formPricePer15 }} €
                <template v-if="formSpanMinutes > 0">
                  ；这一段 {{ minutesLabel(formSpanMinutes) }} 合计 +{{ formTotal | priceDisplay }}
                </template>
              </template>
            </div>
            <div
              class="d-flex flex-wrap"
              style="gap: 0 12px"
            >
              <v-text-field
                v-model="form.effectiveFrom"
                :rules="[() => effectiveRangeError || true]"
                class="sp-field-half"
                clearable
                dense
                hint="含当天，留空 = 不限"
                label="生效开始日期"
                outlined
                persistent-hint
                type="date"
              />
              <v-text-field
                v-model="form.effectiveTo"
                :rules="[() => effectiveRangeError || true]"
                class="sp-field-half"
                clearable
                dense
                hint="含当天，留空 = 不限"
                label="生效结束日期"
                outlined
                persistent-hint
                type="date"
              />
            </div>
            <v-switch
              v-model="form.active"
              class="mt-2"
              dense
              hide-details
              inset
              label="启用"
            />
          </v-form>
          <div class="d-flex mt-6">
            <v-spacer />
            <v-btn
              class="mr-2"
              elevation="0"
              @click="showDialog = false"
            >
              取消
            </v-btn>
            <v-btn
              :loading="dialogLoading"
              color="amber lighten-4 black--text"
              elevation="0"
              @click="save"
            >
              <v-icon left>
                mdi-content-save
              </v-icon>
              保存
            </v-btn>
          </div>
        </div>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import PageHeader from '@/views/widget/typography/PageHeader.vue'
import PrimaryButton from '@/views/AadenPages/components/PrimaryButton.vue'
import LoadingProvider from '@/views/widget/LoadingProvider.vue'
import { sureToDelete } from '@/model/utils/utils'
import {
  deleteSpecialPrice,
  getSpecialPrice, saveSpecialPrice
} from '@/model/dataLayer/service/reservation/specialPrice'
import Table from '@/model/dataLayer/api/tableAndPerson/Table'
import dayjs from 'dayjs'
import BusinessTimeSelect from '@/views/AadenPages/settings/reserveSetting/openTime/BusinessTimeSelect.vue'
import {
  actualLabel,
  loadBusinessHourOffset,
  normalizeHHmm,
  spanMinutes
} from '@/views/AadenPages/settings/reserveSetting/businessTime'

const DateTypeValue = {
  SpecificDate: 'SPECIFIC_DATE',
  Daily: 'DAILY',
  Weekly: 'WEEKLY'
}

const dateTypeItems = [
  { text: '每天', value: DateTypeValue.Daily },
  { text: '每周', value: DateTypeValue.Weekly },
  { text: '指定日期', value: DateTypeValue.SpecificDate }
]

const weekdayItems = [
  { text: '周一', value: 'MONDAY' },
  { text: '周二', value: 'TUESDAY' },
  { text: '周三', value: 'WEDNESDAY' },
  { text: '周四', value: 'THURSDAY' },
  { text: '周五', value: 'FRIDAY' },
  { text: '周六', value: 'SATURDAY' },
  { text: '周日', value: 'SUNDAY' }
]
const WEEKDAY_ZH = ['周日', '周一', '周二', '周三', '周四', '周五', '周六']

// 生效期状态（按今天判断，只用于后台展示；后端按预订日期判断）
const PERIOD_STATUS = {
  always: { text: '一直有效', color: 'grey darken-1' },
  current: { text: '现在生效', color: 'success' },
  upcoming: { text: '以后生效', color: 'blue' },
  expired: { text: '已过期', color: 'grey' }
}

function round2 (n) {
  return Math.round(Number(n) * 100) / 100
}

function round4 (n) {
  return Math.round(Number(n) * 10000) / 10000
}

// 表单里用的字段，保存时不发给后端
const FORM_ONLY_KEYS = ['weekdays', 'specificDate', 'pricePerHour', 'createTimestamp']

export default {
  name: 'SpecialPrice',
  components: {
    BusinessTimeSelect,
    LoadingProvider,
    PrimaryButton,
    PageHeader
  },
  data: function () {
    return {
      dialogLoading: false,
      showDialog: false,
      form: null,
      // 编辑时的原始那一条（保存时整条带回，避免把没在表单里的字段清掉）
      original: null,
      list: [],
      loading: false,
      roomList: [],
      businessHourOffset: null,
      periodFilter: 'all',
      dateTypeItems,
      weekdayItems
    }
  },
  computed: {
    today () {
      return dayjs().format('YYYY-MM-DD')
    },
    timeKindSuffix () {
      return this.businessHourOffset === null ? '（后台时间）' : '（实际时间）'
    },
    hasAnyEffectiveDate () {
      return this.list.some(r => r.effectiveFrom || r.effectiveTo)
    },
    periodFilters () {
      const status = (r) => this.periodInfo(r).status
      const count = (pred) => this.list.filter(pred).length
      return [
        { value: 'all', text: '全部', count: this.list.length },
        { value: 'current', text: '现在生效', count: count(r => ['always', 'current'].includes(status(r))) },
        { value: 'upcoming', text: '以后生效', count: count(r => status(r) === 'upcoming') },
        { value: 'expired', text: '已过期', count: count(r => status(r) === 'expired') }
      ]
    },
    currentFilterText () {
      return this.periodFilters.find(f => f.value === this.periodFilter)?.text ?? ''
    },
    filteredList () {
      if (!this.hasAnyEffectiveDate || this.periodFilter === 'all') return this.list
      return this.list.filter(r => {
        const status = this.periodInfo(r).status
        if (this.periodFilter === 'current') return status === 'always' || status === 'current'
        return status === this.periodFilter
      })
    },
    roomItems () {
      const items = this.roomList.map(it => ({ text: it.name, value: Number(it.id) }))
      // 规则里引用了、但本机桌台里找不到的包厢，也放进来，免得编辑时显示空白
      const known = new Set(items.map(i => i.value))
      this.list.forEach(r => {
        const id = Number(r.tableId)
        if (!known.has(id)) {
          known.add(id)
          items.push({ text: '#' + id, value: id })
        }
      })
      return items
    },
    // 按包厢 → 每天 / 周一…周日 / 指定日期 分组，每组里按开始时间排
    roomGroups () {
      const byRoom = new Map()
      this.filteredList.forEach(r => {
        const id = Number(r.tableId)
        if (!byRoom.has(id)) byRoom.set(id, [])
        byRoom.get(id).push(r)
      })
      const sortRules = (rules) => rules.slice().sort((a, b) =>
        String(normalizeHHmm(a.startTime)).localeCompare(String(normalizeHHmm(b.startTime))) ||
        String(a.effectiveFrom || '').localeCompare(String(b.effectiveFrom || '')) ||
        (a.id || 0) - (b.id || 0))
      const groups = []
      byRoom.forEach((rules, tableId) => {
        const rows = []
        const daily = rules.filter(r => r.dateType === DateTypeValue.Daily)
        if (daily.length) rows.push({ key: 'daily', label: '每天', rules: sortRules(daily) })
        weekdayItems.forEach(w => {
          const ws = rules.filter(r => r.dateType === DateTypeValue.Weekly && String(r.ruleDate).toUpperCase() === w.value)
          if (ws.length) rows.push({ key: w.value, label: w.text, rules: sortRules(ws) })
        })
        const dates = [...new Set(rules.filter(r => r.dateType === DateTypeValue.SpecificDate).map(r => r.ruleDate))].sort()
        dates.forEach(d => {
          const ds = rules.filter(r => r.dateType === DateTypeValue.SpecificDate && r.ruleDate === d)
          rows.push({ key: 'date-' + d, label: d, sub: '指定日期 · ' + this.weekdayOfDate(d), rules: sortRules(ds) })
        })
        // 万一有不认识的类型/周几，也列出来，不让数据「消失」
        const shown = new Set(rows.flatMap(row => row.rules.map(r => r.id)))
        const others = rules.filter(r => !shown.has(r.id))
        if (others.length) rows.push({ key: 'other', label: '其他', rules: sortRules(others) })
        groups.push({ tableId, name: this.tableNameDisplay(tableId), count: rules.length, rows })
      })
      return groups.sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true }))
    },
    formSpanMinutes () {
      return this.form ? spanMinutes(this.form.startTime, this.form.endTime) : 0
    },
    formPricePer15 () {
      return this.isNumber(this.form?.pricePerHour) ? round4(Number(this.form.pricePerHour) / 4).toFixed(4) : ''
    },
    formTotal () {
      return Number(this.formPricePer15) * Math.ceil(this.formSpanMinutes / 15)
    },
    timeRangeError () {
      if (!this.form?.startTime || !this.form?.endTime) return ''
      if (this.formSpanMinutes <= 0) {
        return '结束要晚于开始，且不能跨过营业日开始时间 ' + actualLabel('00:00', this.businessHourOffset)
      }
      return ''
    },
    effectiveRangeError () {
      const from = this.form?.effectiveFrom
      const to = this.form?.effectiveTo
      return from && to && from > to ? '结束日期不能早于开始日期' : ''
    }
  },
  watch: {
    'form.startTime' () { this.revalidate() },
    'form.endTime' () { this.revalidate() },
    'form.effectiveFrom' () { this.revalidate() },
    'form.effectiveTo' () { this.revalidate() }
  },
  mounted () {
    this.reloadData()
  },
  methods: {
    isNumber (v) {
      return v !== null && v !== undefined && v !== '' && Number.isFinite(Number(v))
    },
    revalidate () {
      this.$nextTick(() => {
        (this.$refs.form?.inputs ?? []).forEach(i => {
          if (i.hasInput || i.hasFocused) i.validate()
        })
      })
    },
    tableNameDisplay (id) {
      return this.roomList.find(it => String(it.id) === String(id))?.name ?? ('#' + id)
    },
    weekdayOfDate (d) {
      const day = dayjs(d)
      return day.isValid() ? WEEKDAY_ZH[day.day()] : ''
    },
    // 每小时价格只显示/填写两位小数（每 15 分钟 8.3333 → 每小时 33.33）
    hourlyPrice (r) {
      return round2(Number(r.price) * 4)
    },
    totalPrice (r) {
      return Number(r.price) * Math.ceil(spanMinutes(r.startTime, r.endTime) / 15)
    },
    // 按 15 分钟一格算（结束 23:59 = 营业日结束，算满最后一格）
    minutesLabel (rawMins) {
      const mins = Math.ceil(rawMins / 15) * 15
      const h = Math.floor(mins / 60)
      const m = mins % 60
      if (h && m) return `${h} 小时 ${m} 分`
      if (h) return `${h} 小时`
      return `${m} 分钟`
    },
    durationLabel (r) {
      return this.minutesLabel(spanMinutes(r.startTime, r.endTime))
    },
    timeRangeLabel (r) {
      return actualLabel(r.startTime, this.businessHourOffset) + ' – ' + actualLabel(r.endTime, this.businessHourOffset)
    },
    periodLabel (r) {
      const from = r.effectiveFrom
      const to = r.effectiveTo
      if (from && to) return `${from} 至 ${to}`
      if (from) return `${from} 起`
      if (to) return `到 ${to}`
      return '一直有效'
    },
    periodInfo (r) {
      let status = 'current'
      if (!r.effectiveFrom && !r.effectiveTo) {
        status = 'always'
      } else if (r.effectiveFrom && r.effectiveFrom > this.today) {
        status = 'upcoming'
      } else if (r.effectiveTo && r.effectiveTo < this.today) {
        status = 'expired'
      }
      return { status, ...PERIOD_STATUS[status] }
    },
    openNew () {
      this.original = null
      this.form = {
        tableId: this.roomItems[0]?.value ?? null,
        dateType: DateTypeValue.Weekly,
        weekdays: [],
        specificDate: null,
        startTime: null,
        endTime: null,
        pricePerHour: '',
        effectiveFrom: null,
        effectiveTo: null,
        active: true
      }
      this.showDialog = true
      this.$nextTick(() => this.$refs.form?.resetValidation())
    },
    edit (r) {
      this.original = { ...r }
      const hourly = String(this.hourlyPrice(r))
      this.form = {
        ...r,
        tableId: Number(r.tableId),
        weekdays: r.dateType === DateTypeValue.Weekly && r.ruleDate ? [String(r.ruleDate).toUpperCase()] : [],
        specificDate: r.dateType === DateTypeValue.SpecificDate ? r.ruleDate : null,
        startTime: normalizeHHmm(r.startTime) ?? r.startTime,
        endTime: normalizeHHmm(r.endTime) ?? r.endTime,
        pricePerHour: hourly,
        effectiveFrom: r.effectiveFrom ?? null,
        effectiveTo: r.effectiveTo ?? null,
        active: r.active !== false
      }
      this.showDialog = true
    },
    async remove (item) {
      await sureToDelete(async () => {
        await deleteSpecialPrice(item.id)
        await this.reloadData()
      })
    },
    tip (message) {
      this.$store.commit('showTipsDialog', { tipsMessage: message })
    },
    async save () {
      if (this.$refs.form?.validate() === false) return
      if (this.timeRangeError) return this.tip(this.timeRangeError)
      if (this.effectiveRangeError) return this.tip(this.effectiveRangeError)
      const f = this.form
      // 整条带回：从原来那一条开始，只覆盖表单里的字段（以后后端再加字段也不会被清掉）
      const payload = { ...(this.original ?? {}), ...f }
      FORM_ONLY_KEYS.forEach(k => delete payload[k])
      payload.tableId = Number(f.tableId)
      payload.startTime = normalizeHHmm(f.startTime)
      payload.endTime = normalizeHHmm(f.endTime)
      payload.effectiveFrom = f.effectiveFrom || null
      payload.effectiveTo = f.effectiveTo || null
      payload.active = !!f.active
      // 每小时没改（= 原价格 ×4 保留两位）就原样保留原来的每 15 分钟价格；改了就 ÷4 保留 4 位小数（后端 decimal(10,4)）
      payload.price = this.original && Number(f.pricePerHour) === this.hourlyPrice(this.original)
        ? this.original.price
        : round4(Number(f.pricePerHour) / 4)

      const requests = []
      if (f.dateType === DateTypeValue.Weekly) {
        // 编辑时原来的那一条保留 id（原周几还在就给它，不在就给第一个），其余周几各新建一条
        const days = [...f.weekdays]
        const keepIdFor = this.original?.id
          ? (days.includes(String(this.original.ruleDate).toUpperCase()) ? String(this.original.ruleDate).toUpperCase() : days[0])
          : null
        days.forEach(day => {
          const item = { ...payload, ruleDate: day }
          if (day !== keepIdFor) delete item.id
          requests.push(item)
        })
      } else if (f.dateType === DateTypeValue.SpecificDate) {
        requests.push({ ...payload, ruleDate: f.specificDate })
      } else {
        requests.push({ ...payload, ruleDate: null })
      }

      this.dialogLoading = true
      try {
        for (const item of requests) {
          const res = await saveSpecialPrice(item)
          if (res && res.code !== undefined && res.code !== 200) {
            this.tip(res.message || '保存失败')
            return
          }
        }
        this.showDialog = false
        this.form = null
        this.original = null
      } finally {
        this.dialogLoading = false
        await this.reloadData()
      }
    },
    async reloadData () {
      this.loading = true
      const [priceResult, roomResult, offset] = await Promise.allSettled([
        getSpecialPrice(),
        Table.getList(),
        loadBusinessHourOffset()
      ])
      if (priceResult.status === 'fulfilled') {
        this.list = priceResult.value ?? []
      }
      if (roomResult.status === 'fulfilled') {
        this.roomList = (roomResult.value ?? []).filter(it => it.tableType === 'Room')
      }
      this.businessHourOffset = offset.status === 'fulfilled' ? offset.value : null
      this.loading = false
    }
  }
}
</script>

<style scoped>
.sp-row {
  display: flex;
  flex-wrap: wrap;
  padding: 8px 16px;
}

.sp-row-border {
  border-top: 1px solid rgba(0, 0, 0, 0.06);
}

.sp-row-label {
  width: 110px;
  flex-shrink: 0;
  padding-top: 6px;
}

.sp-row-slots {
  flex: 1;
  min-width: 220px;
}

.sp-slot {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 4px 16px;
  padding: 4px 8px;
  border-radius: 6px;
  cursor: pointer;
}

.sp-slot:hover {
  background: rgba(0, 0, 0, 0.04);
}

.sp-slot-inactive {
  opacity: 0.55;
}

.sp-slot-time {
  min-width: 150px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
}

.sp-slot-price {
  flex: 1;
  min-width: 200px;
  font-variant-numeric: tabular-nums;
}

.sp-field-half {
  flex: 1 1 200px;
}
</style>
